import type { SpotlightListItem, StrapiImage } from '@/services/StrapiService';
import { resolveArticleMediaField } from '@/services/StrapiService';
import { filterVisible, type ViewerContext } from '@/lib/visibility/evaluateVisibility';

export type SpotlightImageLayout = 'none' | 'replaces-title' | 'replaces-badge';

export const SPOTLIGHT_CAPTION_FALLBACK = 'Learn more';

export interface SpotlightCardModel {
    slug: string;
    order: number;
    label?: string;
    title?: string;
    description?: string;
    image?: StrapiImage;
    caption?: string;
    /** True when `caption` was filled in because Strapi had a url and no caption. */
    captionIsFallback: boolean;
    url?: string;
    external: boolean;
    visibility: unknown;
    clickable: boolean;
    imageLayout: SpotlightImageLayout;
}

function text(value: unknown): string | undefined {
    if (typeof value !== 'string') return undefined;
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
}

function orderOf(value: unknown): number {
    return typeof value === 'number' && Number.isFinite(value) ? value : Number.POSITIVE_INFINITY;
}

export function toSpotlightCard(item: SpotlightListItem): SpotlightCardModel {
    const data = item.data ?? {};
    const title = text(data.title);
    const authoredCaption = text(data.caption);
    const url = text(data.url);
    const captionIsFallback = Boolean(url && !authoredCaption);
    const caption = authoredCaption ?? (captionIsFallback ? SPOTLIGHT_CAPTION_FALLBACK : undefined);
    const image = resolveArticleMediaField(data.image);
    const imageLayout: SpotlightImageLayout = !image
        ? 'none'
        : title
            ? 'replaces-badge'
            : 'replaces-title';

    return {
        slug: item.slug,
        order: orderOf(data.order),
        label: text(data.label),
        title,
        description: text(data.description),
        image,
        caption,
        captionIsFallback,
        url,
        external: data.external === true,
        visibility: data.visibility,
        clickable: Boolean(url),
        imageLayout,
    };
}

/** Published cards the viewer is allowed to see, in Strapi `order`. */
export function visibleSpotlights(items: SpotlightListItem[], ctx: ViewerContext): SpotlightCardModel[] {
    const cards = items.map(toSpotlightCard).sort((a, b) => a.order - b.order);
    return filterVisible(cards, ctx, card => card.visibility, card => card.slug);
}
